import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Key, Brain, Send, MapPin, Filter, CheckCircle, ArrowLeft, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Step {
    number: number;
    title: string;
    description: string;
    icon: React.ElementType;
    tips?: string[];
    proPlan?: boolean;
}

const GuidePage_es: React.FC = () => {
    const steps: Step[] = [
        {
            number: 1,
            title: 'Conectar Instancia de WhatsApp',
            description: 'El primer paso es conectar tu WhatsApp al sistema. Ve a la página de Integraciones y escanea el código QR con tu celular. Mantén el celular conectado a internet para garantizar estabilidad.',
            icon: MessageCircle,
            tips: [
                'Usa un número dedicado para mejor organización',
                'Asegúrate de que WhatsApp no está conectado en otro dispositivo',
                'Mantén conexión de internet estable en el celular'
            ]
        },
        {
            number: 2,
            title: 'Conectar SERP API',
            description: 'La SERP API permite realizar búsquedas automatizadas en Google Maps. En la página de Integraciones, introduce tu clave de API de SerpAPI. Puedes obtener una clave gratuita en el sitio oficial de SerpAPI.',
            icon: Key,
            tips: [
                'Crea una cuenta gratuita en serpapi.com',
                'Copia tu API key del dashboard',
                'Pega la clave en el campo apropiado en Integraciones'
            ]
        },
        {
            number: 3,
            title: 'Configurar Agente de Envío',
            description: 'Configura el agente de IA que enviará los mensajes iniciales a los leads. Accede a la sección IA > Agente de Envío y personaliza el tono, el mensaje y la información sobre tu negocio. El agente creará mensajes personalizados automáticamente.',
            icon: Brain,
            tips: [
                'Sé claro y directo en la descripción de tu negocio',
                'Define un tono de voz adecuado para tu público',
                'Prueba diferentes enfoques para ver qué funciona mejor'
            ]
        },
        {
            number: 4,
            title: 'Configurar Agente de Atención (Plan Pro)',
            description: 'Si tienes el Plan Pro, configura también el Agente de Atención que responderá automáticamente a los mensajes recibidos. Accede a IA > Agente de Atención y define cómo debe interactuar con los leads.',
            icon: Sparkles,
            proPlan: true,
            tips: [
                'Configura respuestas para preguntas frecuentes',
                'Define cuándo transferir a atención humana',
                'Mantén un tono profesional y servicial'
            ]
        },
        {
            number: 5,
            title: 'Realizar Búsqueda en Google Maps',
            description: '¡Ahora estás listo para buscar leads! En el Dashboard, escribe el tipo de negocio que buscas (ej: "restaurantes", "gimnasios") y la ubicación deseada. El sistema buscará establecimientos relevantes en Google Maps.',
            icon: MapPin,
            tips: [
                'Usa términos específicos y relevantes',
                'Define la ubicación con precisión',
                'Experimenta con diferentes variaciones de búsqueda'
            ]
        },
        {
            number: 6,
            title: 'Aplicar Filtros en los Resultados',
            description: 'Después de recibir los resultados, usa los filtros para refinar tu lista. Filtra por valoración mínima, número de reseñas, estado de WhatsApp y otros criterios. Esto garantiza que te enfocarás solo en los leads más cualificados.',
            icon: Filter,
            tips: [
                'Prioriza leads con alta valoración',
                'Filtra solo números de WhatsApp válidos',
                'Considera el número de reseñas como indicador de volumen'
            ]
        },
        {
            number: 7,
            title: 'Verificar Números de WhatsApp',
            description: 'El sistema verifica automáticamente si los números encontrados están activos en WhatsApp. Espera a que la verificación se complete antes de enviar mensajes. Los leads con estado "Válido" están listos para recibir mensajes.',
            icon: CheckCircle,
            tips: [
                'Espera a que la verificación automática se complete',
                'Los números "Inválidos" no recibirán mensajes',
                'Los números "Pendientes" aún están siendo verificados'
            ]
        },
        {
            number: 8,
            title: 'Enviar Mensajes',
            description: 'Con los leads verificados y filtrados, selecciona los que deseas contactar y haz clic en "Enviar Mensajes". El Agente de Envío creará mensajes personalizados para cada lead automáticamente. Acompaña el progreso y resultados en el panel.',
            icon: Send,
            tips: [
                'Envía mensajes en horario comercial',
                'Acompaña las tasas de respuesta',
                'Ajusta tu estrategia basándote en los resultados',
                'Sé respetuoso y profesional'
            ]
        }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-6 lg:p-10 max-w-5xl mx-auto flex flex-col gap-8"
        >
            <div>
                <Link to="/documentacao" className="inline-flex items-center gap-2 text-primary hover:underline mb-4">
                    <ArrowLeft className="size-4" />
                    Volver a Documentación
                </Link>

                <motion.h1
                    className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary mb-2"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                >
                    Guía Paso a Paso
                </motion.h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                    Aprende cómo usar el sistema para obtener los mejores resultados
                </p>
            </div>

            {/* Steps */}
            <div className="relative">
                {/* Vertical Line */}
                <div className="absolute left-8 top-12 bottom-12 w-0.5 bg-gradient-to-b from-primary/50 to-primary/10 hidden md:block" />

                <div className="flex flex-col gap-8">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        return (
                            <motion.div
                                key={step.number}
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                                className="relative"
                            >
                                <div className="card p-6 md:ml-16">
                                    {/* Step Number Circle */}
                                    <div className="absolute -left-8 top-6 md:left-0 size-16 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                                        <span className="text-white font-black text-2xl">{step.number}</span>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-6">
                                        {/* Icon */}
                                        <div className="hidden md:flex items-start">
                                            <div className="size-14 rounded-xl bg-primary/10 flex items-center justify-center">
                                                <Icon className="size-7 text-primary" />
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-grow space-y-4 ml-12 md:ml-0">
                                            <div>
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h2 className="text-2xl font-bold text-[#181411] dark:text-white">
                                                        {step.title}
                                                    </h2>
                                                    {step.proPlan && (
                                                        <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full">
                                                            PRO
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                                                    {step.description}
                                                </p>
                                            </div>

                                            {/* Tips */}
                                            {step.tips && step.tips.length > 0 && (
                                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-lg">💡</span>
                                                        <span className="font-semibold text-blue-900 dark:text-blue-300 text-sm">
                                                            Consejos Importantes
                                                        </span>
                                                    </div>
                                                    <ul className="space-y-1.5 ml-1">
                                                        {step.tips.map((tip, tipIndex) => (
                                                            <li key={tipIndex} className="text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
                                                                <span className="text-blue-500 mt-0.5">•</span>
                                                                <span>{tip}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Success Message */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="card p-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800"
            >
                <div className="flex items-start gap-4">
                    <div className="size-12 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                        <CheckCircle className="size-7 text-white" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-green-900 dark:text-green-300 mb-2">
                            ¡Felicidades! ¡Estás listo! 🎉
                        </h3>
                        <p className="text-green-800 dark:text-green-200">
                            Siguiendo estos pasos, serás capaz de encontrar leads cualificados y hacer contactos de forma eficiente y automatizada. Recuerda siempre mantener un enfoque profesional y respetuoso en tus mensajes.
                        </p>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default GuidePage_es;
